<template>
  <el-dropdown trigger="click" @command="switchTenant" @visible-change="refreshWhenOpened">
    <button type="button" class="admin-tenant-switch" :aria-label="t('admin.tenantSwitch.label')">
      <el-icon><OfficeBuilding /></el-icon>
      <span>{{ currentName }}</span>
      <el-icon><ArrowDown /></el-icon>
    </button>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="tenant in tenants"
          :key="String(tenant.tenantCode)"
          :command="String(tenant.tenantCode)"
          :disabled="String(tenant.tenantCode) === currentCode"
        >
          {{ tenant.tenantName }}（{{ tenant.tenantCode }}）
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ArrowDown, OfficeBuilding } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'

import { tenantApi, type TenantListItem } from '@/api/admin/tenant'
import { readAdminTenantCode, writeAdminTenantCode } from '@/apps/admin/utils/tenant-preference'

const currentCode = ref(readAdminTenantCode())
const { t } = useI18n()
const currentName = ref(t('admin.tenantSwitch.fallback', { code: currentCode.value }))
const tenants = ref<TenantListItem[]>([])
const loading = ref(false)

const load = async (): Promise<void> => {
  loading.value = true
  try {
    const [items, current] = await Promise.all([tenantApi.list(), tenantApi.current()])
    tenants.value = items
    currentName.value = current.tenantName || t('admin.tenantSwitch.fallback', { code: currentCode.value })
  } catch {
    ElMessage.warning(t('admin.tenantSwitch.loadFailed'))
  } finally {
    loading.value = false
  }
}

const switchTenant = (code: string): void => {
  if (code === currentCode.value) return
  writeAdminTenantCode(code)
  window.location.assign('/admin/dashboard')
}

const refreshWhenOpened = (visible: boolean): void => {
  if (visible && !loading.value) void load()
}

onMounted(() => { void load() })
</script>

<style scoped>
.admin-tenant-switch {
  display: inline-flex;
  height: 38px;
  padding: 0 8px;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

.admin-tenant-switch:hover { opacity: 0.88; }
.admin-tenant-switch .el-icon:last-child { font-size: 12px; }
</style>
